import { createApi } from "@reduxjs/toolkit/query/react"

import { baseQuery } from "./baseQuery"
import {
  groupTimeSlotsByDate,
  transformDateOverridesToUTC,
  transformWeeklyScheduleToUTC,
  transformWeeklyScheduleToLocal,
} from "@/lib/utils/time-slot-utils"

export type TAvailabilityTimeSlot = {
  start_time: string // "HH:mm:ss" format
  end_time: string // "HH:mm:ss" format
}

export type TAvailabilityResponse = {
  weekly_schedule: {
    monday: TAvailabilityTimeSlot[]
    tuesday: TAvailabilityTimeSlot[]
    wednesday: TAvailabilityTimeSlot[]
    thursday: TAvailabilityTimeSlot[]
    friday: TAvailabilityTimeSlot[]
    saturday: TAvailabilityTimeSlot[]
    sunday: TAvailabilityTimeSlot[]
  }
  date_overrides: Record<string, TAvailabilityTimeSlot[]>
  date_exclusions: string[]
  metadata: Record<string, unknown>
}

export type TAvailabilityRequest = {
  weekly_schedule?: {
    monday?: TAvailabilityTimeSlot[]
    tuesday?: TAvailabilityTimeSlot[]
    wednesday?: TAvailabilityTimeSlot[]
    thursday?: TAvailabilityTimeSlot[]
    friday?: TAvailabilityTimeSlot[]
    saturday?: TAvailabilityTimeSlot[]
    sunday?: TAvailabilityTimeSlot[]
  }
  date_overrides?: Record<string, TAvailabilityTimeSlot[]>
  date_exclusions?: string[]
  metadata?: Record<string, unknown>
}

export const availabilityApi = createApi({
  reducerPath: "availabilityApi",
  baseQuery,
  tagTypes: ["Availability"],
  endpoints: (builder) => ({
    // Check if availability exists
    checkAvailabilityExists: builder.query<{ exists: boolean }, void>({
      query: () => "/availability/exists",
      providesTags: ["Availability"],
    }),

    // Get availability
    getAvailability: builder.query<TAvailabilityResponse, void>({
      query: () => "/availability",
      providesTags: ["Availability"],
      transformResponse: (response: any) => {
        return {
          ...response,
          weekly_schedule: response.weekly_schedule
            ? transformWeeklyScheduleToLocal(response.weekly_schedule)
            : response.weekly_schedule,
          date_overrides: groupTimeSlotsByDate(response.date_overrides),
        }
      },
    }),

    // Update/Create availability
    updateAvailability: builder.mutation<
      TAvailabilityResponse,
      TAvailabilityRequest
    >({
      query: (payload) => {
        // Transform weekly_schedule and date_overrides from local time to UTC
        const transformedPayload = {
          ...payload,
          weekly_schedule: payload.weekly_schedule
            ? transformWeeklyScheduleToUTC(payload.weekly_schedule)
            : undefined,
          date_overrides: payload.date_overrides
            ? transformDateOverridesToUTC(payload.date_overrides)
            : undefined,
        }

        return {
          url: "/availability",
          method: "POST",
          body: transformedPayload,
        }
      },
      invalidatesTags: ["Availability"],
    }),
  }),
})

export const {
  useCheckAvailabilityExistsQuery,
  useLazyCheckAvailabilityExistsQuery,
  useGetAvailabilityQuery,
  useLazyGetAvailabilityQuery,
  useUpdateAvailabilityMutation,
} = availabilityApi
